import { useEffect } from 'react'
import { useListView } from './core/ListViewProvider'
import { useQueryResponse, useQueryResponseData, useQueryResponseLoading } from './core/QueryResponseProvider'
import { Application } from './core/_models'
import { User, Briefcase, ChevronRight } from 'lucide-react'
import { ApplicationCardSkeleton } from '../../../../../_component/Skeleton'

export const ApplicationSidebar = () => {
  const applications = useQueryResponseData() as Application[]
  const isLoading = useQueryResponseLoading()
  const {state} = useQueryResponse()
  const { itemIdToView, setItemIdToView, selected, onSelect } = useListView()

  const selectedJobId = (state?.filter as any)?.job_id

  useEffect(() => {
    if (!itemIdToView && applications.length > 0) {
      setItemIdToView(applications[0].id)
    }
  }, [applications, itemIdToView, setItemIdToView])

  if (isLoading) {
    return (
      <div className='flex flex-col h-full bg-white'>
        <div className='p-4 border-b border-gray-200 bg-gray-50'>
          <div className='h-4 w-24 bg-gray-200 animate-pulse rounded'></div>
        </div>
        {[1, 2, 3, 4, 5, 6].map(i => <ApplicationCardSkeleton key={i} />)}
      </div>
    )
  }

  return (
    <div className='flex flex-col h-full bg-white'>
      <div className='p-4 border-b border-gray-200 bg-gray-50'>
        <h3 className='text-sm font-bold text-gray-700 uppercase tracking-wider'>
          Applicants ({applications.length})
        </h3>
      </div>
      <div className='flex-1 overflow-y-auto'>
        {applications.length === 0 ? (
          <div className='p-8 text-center text-gray-500'>
            {selectedJobId ? 'No applicants found for this job' : 'No applications found'}
          </div>
        ) : (
          <div className='divide-y divide-gray-100'>
            {applications.map((app) => {
              const isActive = itemIdToView === app.id
              const isSelected = selected.includes(app.id)
              return (
                <div
                  key={app.id}
                  className={`
                    group p-4 cursor-pointer transition-all duration-200 flex items-start gap-4 relative
                    ${isActive ? 'bg-teal-50 border-r-4 border-[#008080]' : 'hover:bg-gray-50'}
                  `}
                >
                  <div className='pt-1' onClick={(e) => e.stopPropagation()}>
                    <input
                      type='checkbox'
                      className='form-check-input w-4 h-4 text-[#008080] border-gray-300 rounded focus:ring-[#008080]'
                      checked={isSelected}
                      onChange={() => onSelect(app.id)}
                    />
                  </div>
                  <div className='flex-1 flex gap-4 min-w-0' onClick={() => setItemIdToView(app.id)}>
                    <div
                      className={`
                      w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
                      ${isActive
                          ? 'bg-[#008080] text-white'
                          : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200'
                        }
                    `}
                    >
                      <User size={24} />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center justify-between mb-1'>
                        <h4
                          className={`text-sm font-bold truncate ${isActive ? 'text-[#008080]' : 'text-gray-900'
                            }`}
                        >
                          {app.full_name}
                        </h4>
                        <ChevronRight
                          size={16}
                          className={isActive ? 'text-[#008080]' : 'text-gray-300'}
                        />
                      </div>
                      <div className='flex items-center gap-2 text-xs text-gray-500 mb-2'>
                        <Briefcase size={12} className='text-gray-400' />
                        <span className='truncate'>{app.job?.title || 'Unknown Job'}</span>
                      </div>
                      <span
                        className={`
                          inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide
                          ${getStatusStyles(app.status)}
                      `}
                      >
                        {app.status}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const getStatusStyles = (status?: string) => {
  switch (status?.toLowerCase()) {
    case 'applied':
      return 'bg-blue-100 text-blue-700'
    case 'read':
      return 'bg-purple-100 text-purple-700'
    case 'accepted':
      return 'bg-green-100 text-green-700'
    case 'declined':
      return 'bg-red-100 text-red-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}
